import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getHospitalById } from '../../data/hospitals';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function HospitalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const hospital = id ? getHospitalById(id) : undefined;

  useEffect(() => {
    if (!hospital) return;

    const isUz = i18n.language === 'uz';
    const hospitalName = isUz ? hospital.name : hospital.nameRu;
    const hospitalAddress = isUz ? hospital.address : hospital.addressRu;
    const hospitalDescription = isUz ? hospital.description : hospital.descriptionRu;
    const hospitalSpecialties = isUz ? hospital.specialties : hospital.specialtiesRu;

    const title = `${hospitalName} - MediVoyage`;
    const description = hospitalDescription.substring(0, 160);

    document.title = title;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.medivoyageuz.com';

    const schemaHospital = {
      '@context': 'https://schema.org',
      '@type': 'Hospital',
      'name': hospitalName,
      'url': `${siteUrl}/hospital/${hospital.id}`,
      'description': hospitalDescription,
      'image': hospital.image,
      'address': {
        '@type': 'PostalAddress',
        'addressCountry': 'KR',
        'addressLocality': 'Seoul',
        'streetAddress': hospitalAddress,
      },
      'telephone': hospital.phone,
      'medicalSpecialty': hospitalSpecialties,
    };

    let scriptHospital = document.getElementById('schema-hospital');
    if (!scriptHospital) {
      scriptHospital = document.createElement('script');
      scriptHospital.id = 'schema-hospital';
      scriptHospital.type = 'application/ld+json';
      document.head.appendChild(scriptHospital);
    }
    scriptHospital.textContent = JSON.stringify(schemaHospital);

    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = `${siteUrl}/hospital/${hospital.id}`;

    return () => {
      scriptHospital?.remove();
    };
  }, [hospital, i18n.language]);

  if (!hospital) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {i18n.language === 'ru' ? 'Больница не найдена' : 'Shifoxona topilmadi'}
            </h1>
            <button
              onClick={() => navigate('/')}
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              {t('back_to_list')}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isRu = i18n.language === 'ru';
  const hospitalName = isRu ? hospital.nameRu : hospital.name;
  const hospitalAddress = isRu ? hospital.addressRu : hospital.address;
  const hospitalDescription = isRu ? hospital.descriptionRu : hospital.description;
  const hospitalSpecialties = isRu ? hospital.specialtiesRu : hospital.specialties;

  const handleContactClick = () => {
    navigate('/#contact');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const departmentSectionTitle = isRu ? 'Отделения больницы' : 'Kasalxona bo\'limlari';

  const deptColors = [
    'bg-teal-50 text-teal-600 border-teal-100',
    'bg-rose-50 text-rose-600 border-rose-100',
    'bg-amber-50 text-amber-600 border-amber-100',
    'bg-sky-50 text-sky-600 border-sky-100',
    'bg-violet-50 text-violet-600 border-violet-100',
    'bg-emerald-50 text-emerald-600 border-emerald-100',
    'bg-orange-50 text-orange-600 border-orange-100',
    'bg-cyan-50 text-cyan-600 border-cyan-100',
    'bg-pink-50 text-pink-600 border-pink-100',
    'bg-lime-50 text-lime-600 border-lime-100',
    'bg-indigo-50 text-indigo-600 border-indigo-100',
    'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100',
    'bg-red-50 text-red-600 border-red-100',
    'bg-yellow-50 text-yellow-600 border-yellow-100',
    'bg-green-50 text-green-600 border-green-100',
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <div className="relative h-96 bg-gray-900">
        <img
          src={hospital.image}
          alt={hospitalName}
          className="w-full h-full object-cover object-center opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {hospitalName}
            </h1>
            <div className="flex items-center justify-center text-white/90 text-base">
              <i className="ri-map-pin-line mr-2"></i>
              <span>{hospitalAddress}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-teal-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            <span className="font-medium">{t('back_to_list')}</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-8">

            {/* Specialties Tags */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-stethoscope-line mr-3 text-teal-600"></i>
                {t('hospital_detail_specialties')}
              </h2>
              <div className="flex flex-wrap gap-3">
                {hospitalSpecialties.map((specialty, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-teal-50 text-teal-700 text-sm font-medium rounded-lg border border-teal-100"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>

            {/* Departments Section — only for suncheonhyang */}
            {hospital.departments && hospital.departments.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-hospital-line mr-3 text-teal-600"></i>
                  {departmentSectionTitle}
                </h2>
                <p className="text-sm text-gray-500 mb-8">
                  {isRu
                    ? 'Полный список медицинских отделений и специализаций'
                    : 'Barcha tibbiy bo\'limlar va mutaxassisliklar ro\'yxati'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {hospital.departments.map((dept, index) => {
                    const colorClass = deptColors[index % deptColors.length];
                    const [bgColor, textColor, borderColor] = colorClass.split(' ');
                    return (
                      <div
                        key={index}
                        className="group flex items-start gap-4 p-4 rounded-xl border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all duration-200 bg-white cursor-default"
                      >
                        <div className={`w-11 h-11 flex items-center justify-center rounded-lg flex-shrink-0 border ${bgColor} ${textColor} ${borderColor} group-hover:scale-110 transition-transform`}>
                          <i className={`${dept.icon} text-xl`}></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 leading-snug mb-1">
                            {isRu ? dept.nameRu : dept.name}
                          </p>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            {isRu ? dept.descRu : dept.desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* About */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-information-line mr-3 text-teal-600"></i>
                {t('hospital_detail_about')}
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                {hospitalDescription}
              </p>
            </div>

            {/* Location */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-map-pin-line mr-3 text-teal-600"></i>
                {t('hospital_detail_location')}
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-map-2-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('hospital_detail_address')}</h3>
                    <p className="text-gray-600">{hospitalAddress}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {t('hospital_detail_contact_info')}
              </h3>

              <div className="space-y-4 mb-6">
                {hospital.phone && (
                  <a
                    href={`tel:${hospital.phone}`}
                    className="flex items-center text-gray-700 hover:text-teal-600 transition-colors group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 group-hover:bg-teal-50 text-gray-600 group-hover:text-teal-600 rounded-lg mr-3 transition-colors">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-0.5">{t('hospital_detail_phone')}</p>
                      <p className="text-sm font-medium">{hospital.phone}</p>
                    </div>
                  </a>
                )}

                <div className="flex items-start text-gray-700">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg mr-3 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-0.5">{t('hospital_detail_address')}</p>
                    <p className="text-sm font-medium leading-relaxed">{hospitalAddress}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <div className="bg-teal-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-customer-service-2-line mr-2 text-teal-600"></i>
                    {t('hospital_detail_consultation')}
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('hospital_detail_consultation_desc')}
                  </p>
                </div>
                <button
                  onClick={handleContactClick}
                  className="w-full bg-teal-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line mr-2"></i>
                  {t('hospital_detail_book_consultation')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
